import { useTranslation } from 'react-i18next';
import { PublicLayout } from 'hoc';
import { useAutoUpdate } from 'hooks/useAutoUpdate';
import { Button } from 'components';
import { useAudioDevice } from 'providers/AudioDeviceProvider';
import { useAuth } from 'providers/AuthProvider';
import { displayDeviceLabel } from 'utils/audio/displayDeviceLabel';
import {
  DeviceList,
  DeviceListHeading,
  DeviceListItem,
  SectionDivider,
  UpdatesContainer,
  UpdateSection,
  UpdateStatus,
  ProgressContainer,
  ProgressBar,
  Wrapper,
} from './systemInformation.styles';
import { ReleaseNotesAccordion } from 'widgets/systemInformations/ReleaseNotesAccordion';

const AudioDevices = ({
  heading,
  emptyText,
  devices,
}: {
  heading: string;
  emptyText: string;
  devices: { deviceId: string; label: string }[];
}) => (
  <>
    <DeviceListHeading>{heading}</DeviceListHeading>
    {devices.length > 0 ? (
      <DeviceList>
        {devices.map((device) => (
          <DeviceListItem key={device.deviceId}>{displayDeviceLabel(device.label)}</DeviceListItem>
        ))}
      </DeviceList>
    ) : (
      <UpdateStatus>{emptyText}</UpdateStatus>
    )}
  </>
);

export default function SystemInformationScreen() {
  const { t } = useTranslation();
  const audio = useAudioDevice();
  const { isAuthenticated } = useAuth();
  const environmentLabel = t(`audio.systemInformation.modes.${audio.environment}`);

  // The screen is reachable before the login; release notes and updates need a session.
  const {
    status,
    currentVersion,
    updateInfo,
    downloadProgress,
    error,
    isElectron,
    isDev,
    isManagedInstall,
    checkForUpdates,
    downloadUpdate,
    installUpdate,
  } = useAutoUpdate({ enabled: isAuthenticated });

  const getUpdateStatusText = () => {
    switch (status) {
      case 'checking':
        return t('updates.checking');
      case 'available':
        return t('updates.available', { version: updateInfo?.version ?? '' });
      case 'not-available':
        return t('updates.notAvailable');
      case 'downloading':
        return t('updates.downloading', { percent: downloadProgress?.percent ?? 0 });
      case 'downloaded':
        return t('updates.downloaded');
      case 'error':
        return error ? `${t('updates.updateError')}: ${error}` : t('updates.errorGeneric');
      default:
        return '';
    }
  };

  return (
    <PublicLayout title={t('systemInformation.title')} hasBackButton={true}>
      <Wrapper>
        <UpdatesContainer>
          <UpdateSection>
            {!isAuthenticated ? (
              <>
                <h2>{t('updates.softwareUpdate')}</h2>
                <UpdateStatus>{t('updates.loginRequired')}</UpdateStatus>
              </>
            ) : isElectron && isDev ? (
              <UpdateStatus>{t('updates.onlyInDevMode')}</UpdateStatus>
            ) : isElectron && isManagedInstall ? (
              <>
                <h2>{t('updates.currentVersion')}</h2>
                <UpdateStatus>{currentVersion || '...'}</UpdateStatus>
                <h2>{t('updates.softwareUpdate')}</h2>
                <UpdateStatus>{t('updates.managedByBaramundi')}</UpdateStatus>
              </>
            ) : isElectron ? (
              <>
                <h2>{t('updates.currentVersion')}</h2>
                <UpdateStatus>{currentVersion || '...'}</UpdateStatus>
                <h2>{t('updates.softwareUpdate')}</h2>
                <UpdateStatus>{getUpdateStatusText()}</UpdateStatus>

                {status === 'idle' || status === 'not-available' || status === 'error' ? (
                  <Button
                    onClick={() => {
                      void checkForUpdates();
                    }}
                  >
                    {t('updates.searchForUpdates')}
                  </Button>
                ) : status === 'available' ? (
                  <Button
                    onClick={() => {
                      void downloadUpdate();
                    }}
                  >
                    {t('updates.downloadUpdate')}
                  </Button>
                ) : status === 'downloading' ? (
                  <ProgressContainer>
                    <UpdateStatus>
                      {downloadProgress
                        ? `${downloadProgress.percent}% (${Math.round(
                            downloadProgress.transferred / 1024 / 1024
                          )} MB / ${Math.round(downloadProgress.total / 1024 / 1024)} MB)`
                        : t('updates.downloadingInBackground')}
                    </UpdateStatus>
                    {downloadProgress && <ProgressBar value={downloadProgress.percent} max={100} />}
                  </ProgressContainer>
                ) : status === 'downloaded' ? (
                  <Button
                    onClick={() => {
                      void installUpdate();
                    }}
                  >
                    {t('updates.installAndRestart')}
                  </Button>
                ) : null}
              </>
            ) : (
              <UpdateStatus>{t('updates.onlyInDesktopApp')}</UpdateStatus>
            )}
          </UpdateSection>
          <hr className="border-border" />
          <UpdateSection>
            <h2>{t('audio.systemInformation.heading')}</h2>
            <UpdateStatus>{`${t('audio.systemInformation.mode')}: ${environmentLabel}`}</UpdateStatus>
            {audio.mode === 'active' && (
              <>
                <AudioDevices
                  heading={t('audio.systemInformation.devices')}
                  emptyText={t('audio.systemInformation.noDevices')}
                  devices={audio.devices}
                />
                <AudioDevices
                  heading={t('audio.systemInformation.outputDevices')}
                  emptyText={t('audio.systemInformation.noOutputDevices')}
                  devices={audio.outputs}
                />
              </>
            )}
          </UpdateSection>
          <SectionDivider />
          <UpdateSection>
            {isAuthenticated ? (
              <ReleaseNotesAccordion />
            ) : (
              <>
                <h2>{t('releaseNotes.title')}</h2>
                <UpdateStatus>{t('releaseNotes.loginRequired')}</UpdateStatus>
              </>
            )}
          </UpdateSection>
        </UpdatesContainer>
      </Wrapper>
    </PublicLayout>
  );
}
